import { throttle } from 'es-toolkit';
import posthog from 'posthog-js';
import { v4 } from 'uuid';

import SimplifiedMic from '~/modules/game-engine/input/simplified-mic';
import events from '~/modules/game-events/game-events';
import { PingPongTracker } from '~/modules/network/rpc/ping-pong-tracker';
import { createRpcProxy } from '~/modules/network/rpc/rpc-client';
import { ExtractContract } from '~/modules/network/rpc/types';
import { ClientTransport } from '~/modules/remote-mic/network/client/transport/interface';
import { LanRelayClientTransport } from '~/modules/remote-mic/network/client/transport/lan-relay-client';
import { MicRelayClientTransport } from '~/modules/remote-mic/network/client/transport/mic-relay-client';
import { PartyKitClientTransport } from '~/modules/remote-mic/network/client/transport/party-kit-client';
import { RelayClientTransport } from '~/modules/remote-mic/network/client/transport/relay-client-transport';
import { WebSocketClientTransport } from '~/modules/remote-mic/network/client/transport/web-socket-client';
import { NetworkMessages } from '~/modules/remote-mic/network/messages';
import { serverHandlers } from '~/modules/remote-mic/network/server/server-handlers';
import Listener from '~/modules/utils/listener';
import { roundTo } from '~/modules/utils/round-to';
import storage from '~/modules/utils/storage';
import { RemoteMicrophoneLagSetting } from '~/routes/settings/settings-state';

import { dispatchClientCall, registerClientHandler } from './client-handlers';
import { subscriptionManager } from './subscriptions';

export const MIC_ID_KEY = 'MIC_CLIENT_ID';
/** Tries at opening the phone's mic for one start the game asked for, a growing gap apart. */
const MIC_START_ATTEMPTS = 6;

export type transportCloseReason = string;
export type transportErrorReason = string;

export type ServerRpc = ExtractContract<typeof serverHandlers>;

/**
 * How many times a first join is retried before the phone is told the game is offline. The relay
 * answers a room whose game is between sockets exactly as it answers a code nobody is hosting, so
 * a phone joining during a reconnect has to outlast it rather than believe it.
 */
const JOIN_ATTEMPTS = 3;

/**
 * How long the phone waits for a pong before deciding the link is dead. It pings every two seconds,
 * so this is five unanswered pings - long enough that a phone briefly out of range or a game busy
 * loading a song is not thrown off, short enough that a dead link is not sat on.
 */
const PONG_DEADLINE_MS = 10_000;

export class NetworkClient extends Listener<[NetworkMessages]> {
  private transport: ClientTransport | undefined;
  private clientId = storage.getItem(MIC_ID_KEY);

  private roomId: string | null = null;

  private reconnecting = false;
  private connected = false;
  // The name currently registered (or being registered) with the server — kept up to date by both
  // connect() and renameSelf() so reconnects always re-register with the latest name, not the one
  // captured by the original connect() call
  private currentName = '';

  private frequencies: number[] = [];

  private sendFrequencies = throttle((volume: number) => {
    const freqs = this.frequencies.map((freq) => roundTo(freq, 2));
    this.frequencies.length = 0;
    this.transport?.sendEvent({ t: 'freq', 0: freqs, 1: roundTo(volume, 4) } as NetworkMessages);
  }, 50);

  // Chunk frequencies and send them in packages
  // One package throttled with ~60Hz contains ~10 frequencies
  private onFrequencyUpdate = throttle((freq: number, volume: number) => {
    this.frequencies.push(freq);
    this.sendFrequencies(volume);
  }, 1_000 / 60);

  constructor() {
    super();

    // Register imperative client handlers once so they are not duplicated on reconnect.
    // These use a Set internally, so registering the same function reference is idempotent,
    // but since arrow functions inside connectToServer would create new references each time,
    // we register them here with stable references.
    registerClientHandler('startMonitor', () => {
      SimplifiedMic.removeListener(this.onFrequencyUpdate);
      SimplifiedMic.addListener(this.onFrequencyUpdate);
      void this.startMicFor(++this.monitorRequest, 1);
    });
    registerClientHandler('stopMonitor', () => {
      this.monitorRequest++;
      SimplifiedMic.removeListener(this.onFrequencyUpdate);
      SimplifiedMic.stopMonitoring();
      events.remoteMicMonitoringStopped.dispatch();
    });
    registerClientHandler('reload', () => {
      global.removeEventListener('beforeunload', this.disconnect);
      this.transport?.sendEvent({ t: 'unregister' } as NetworkMessages);
      storage.session.setItem('reload-mic-request', '1');
      document.getElementById('phone-ui-container')?.remove();
      global.location?.reload();
    });

    // Bridge handlers: dispatch old GameEvents so legacy consumers keep working.
    // These will be removed once all consumers are migrated to useClientHandler (Phase 6).
    registerClientHandler('setPlayerNumber', (playerNumber) => {
      events.remoteMicPlayerSet.dispatch(playerNumber);
    });
    registerClientHandler('setPermissions', (level) => {
      events.remoteMicPermissionsSet.dispatch(level);
    });
    registerClientHandler('requestReadiness', (secondsLeft) => {
      events.remoteReadinessRequested.dispatch(secondsLeft);
    });

    global?.addEventListener?.('online', this.retryNow);
    global?.document?.addEventListener?.('visibilitychange', () => {
      if (!global.document.hidden) this.retryNow();
    });
  }

  // RPC proxy — call server methods as if they were local async functions.
  // The onDisconnect callback rejects any in-flight request immediately when the connection drops,
  // rather than waiting for the 10-second timeout.
  public readonly rpc: ServerRpc = createRpcProxy<typeof serverHandlers>(
    () => this.transport,
    (callback) => {
      const handler = (status: string) => {
        if (status === 'disconnected' || status === 'reconnecting' || status === 'error') {
          callback();
        }
      };
      events.karaokeConnectionStatusChange.subscribe(
        handler as Parameters<typeof events.karaokeConnectionStatusChange.subscribe>[0],
      );
      return () =>
        events.karaokeConnectionStatusChange.unsubscribe(
          handler as Parameters<typeof events.karaokeConnectionStatusChange.subscribe>[0],
        );
    },
  );

  public getClientId = () => this.clientId;
  private setClientId = (id: string) => {
    this.clientId = id;
    storage.setItem(MIC_ID_KEY, id);
  };

  // A no-op while not yet connected — the next real connect() call already sends the current name
  public renameSelf = (name: string) => {
    if (this.connected) {
      this.currentName = name;
      this.rpc.players.setName(name).catch((e) => console.warn('Failed to rename mic', e));
    }
  };

  /** Join a room. Every caller-driven join starts with a full budget of attempts. */
  public connect = (roomId: string, name: string, silent: boolean) => {
    this.joinAttempt = 0;
    this.retryAttempt = 0;
    this.attemptConnect(roomId, name, silent);
  };

  private attemptConnect = (roomId: string, name: string, silent: boolean) => {
    const lcRoomId = roomId.toLowerCase();
    this.currentName = name;
    this.clearRetry();
    if (this.transport) {
      this.transport.clearAllListeners();
      this.pingPong.stop();
      this.transport.close();
    }
    this.transport = lcRoomId.startsWith('w')
      ? new WebSocketClientTransport()
      : lcRoomId.startsWith('l')
        ? new LanRelayClientTransport()
        : lcRoomId.startsWith('m')
          ? new MicRelayClientTransport()
          : new PartyKitClientTransport();
    if (this.transport instanceof RelayClientTransport) this.transport.onRoomBack = this.handleRoomBack;

    if (this.clientId === null) this.setClientId(v4());
    this.roomId = lcRoomId;

    if (this.transport.isConnected()) {
      console.log('not reconnecting', this.transport);
      return;
    }

    if (!this.reconnecting) {
      events.karaokeConnectionStatusChange.dispatch('connecting');
    }
    this.transport.connect(
      this.clientId!,
      this.roomId,
      () => {
        this.connectToServer(lcRoomId, silent);
      },
      (reason) => {
        // Stop the ping loop as soon as the transport closes — it re-arms itself via
        // connectToServer() on reconnect, but must not keep scheduling pings against a dead
        // transport in the meantime (or forever, on a final disconnect).
        this.pingPong.stop();

        if (reason === 'unavailable-id') {
          // create new id if the old one is taken
          this.setClientId(v4());
          this.attemptConnect(lcRoomId, name, silent);
          return;
        }
        global.removeEventListener('beforeunload', this.disconnect);

        const wasConnected = this.connected;
        this.connected = false;

        events.remoteMicPlayerSet.dispatch(null);
        // Clear the cached keyboard layout so the subscription-based consumers show nothing while disconnected
        subscriptionManager.handlePublish('keyboard-layout', undefined);

        this.monitorRequest++;
        SimplifiedMic.removeListener(this.onFrequencyUpdate);
        SimplifiedMic.stopMonitoring();

        if (reason === 'player-removed') {
          this.reconnecting = false;
          events.karaokeConnectionStatusChange.dispatch('disconnected');
          return;
        }

        if (wasConnected || this.reconnecting) {
          // A room this phone has already been in is worth waiting for. The relay drops every
          // phone in a room the moment the game's own socket goes and refuses them until it is
          // back, so being thrown out says nothing about whether the party is over.
          events.karaokeConnectionStatusChange.dispatch(wasConnected ? 'disconnected' : 'reconnecting');
          this.reconnecting = true;
          this.scheduleRetry(() => {
            if (!this.reconnecting) return;
            events.karaokeConnectionStatusChange.dispatch('reconnecting');
            this.attemptConnect(lcRoomId, this.currentName, false);
          });
          return;
        }

        // Never connected. A game between sockets is refused with the same `peer-unavailable` as a
        // code that was mistyped, so the two are told apart by trying again rather than by the
        // reason - a phone joining while the game reconnects would otherwise be told the game is
        // offline when it is a second away.
        this.joinAttempt++;
        if (this.joinAttempt < JOIN_ATTEMPTS) {
          events.karaokeConnectionStatusChange.dispatch('connecting');
          this.scheduleRetry(() => this.attemptConnect(lcRoomId, name, silent));
          return;
        }

        events.karaokeConnectionStatusChange.dispatch('error', reason);
        posthog.capture('remote_mic_connection_error', { reason, transport: this.roomId?.charAt(0) });
      },
      console.warn,
    );
  };

  private pingPong = new PingPongTracker({
    // 999 until the first pong comes back, so the readout never claims a 0 ms round trip
    initialLatency: 999,
    onMeasurement: (ping) => this.reportPing(ping),
    maxWaitMs: PONG_DEADLINE_MS,
    onMissedPong: () => this.handleSilentLink(),
  });

  /**
   * Nothing has come back from the game in a long while. The socket itself may still read as open -
   * a link that dies between here and the relay does not close, it just stops carrying - so the
   * phone hangs up on it deliberately and lets the reconnect path take over. Without this it sat on
   * a "connected" screen with a dead socket until somebody thought to reload it.
   */
  private handleSilentLink = () => {
    if (!this.connected) return;

    this.pingPong.stop();
    if (this.transport?.hangUp) {
      this.transport.hangUp('unresponsive');
    } else {
      this.transport?.close();
    }
  };

  // Read by the remote-mic ping readout, which counts up while a pong is overdue
  public get latency() {
    return this.pingPong.getLatency();
  }
  public get pingStart() {
    return this.pingPong.getPingStartedAt();
  }
  public get pinging() {
    return this.pingPong.isPinging();
  }

  private ping = () => {
    this.transport?.sendEvent({ t: 'ping' } as NetworkMessages);
  };

  private reportPing = throttle((ping: number) => posthog.capture('remote_mic_ping', { ping }), 60_000);

  public connectToServer = (_roomId: string, silent: boolean) => {
    const resumed = this.reconnecting;
    this.connected = true;
    this.reconnecting = false;
    this.joinAttempt = 0;
    this.retryAttempt = 0;
    events.karaokeConnectionStatusChange.dispatch('connected');
    posthog.capture('remote_mic_connection_successful', { transport: this.roomId?.charAt(0) });
    this.sendRegister(silent, resumed);
    this.pingPong.start(this.ping);
    global?.addEventListener('beforeunload', this.disconnect);

    // Wire subscription manager to send rpc-sub/rpc-unsub via the current transport
    subscriptionManager.setSendFunctions(
      (channel) => this.transport?.sendEvent({ t: 'rpc-sub', channel }),
      (channel) => this.transport?.sendEvent({ t: 'rpc-unsub', channel }),
    );

    this.transport!.addListener((data) => {
      const type = data.t;
      this.onUpdate(data);

      if (type === 'rpc-call') {
        // Server-initiated call — dispatch to registered handlers
        dispatchClientCall(data.method, data.args);
      } else if (type === 'rpc-pub') {
        // Server pushed subscription data
        subscriptionManager.handlePublish(data.channel, data.data);
      } else if (type === 'pong') {
        this.pingPong.handlePong();
      } else if (type === 'ping') {
        this.transport?.sendEvent({ t: 'pong' } as NetworkMessages);
      }
      // rpc-res messages are handled internally by createRpcProxy listeners — no action needed here
    });
  };

  /** Bumped by every start and stop the game asks for, so a retry only serves the latest start. */
  private monitorRequest = 0;

  // A mic that fails to open (busy, still held by a call) is tried again rather than left off for the song.
  private startMicFor = async (request: number, attempt: number) => {
    const started = await SimplifiedMic.tryStartMonitoring();
    if (request !== this.monitorRequest) return;
    if (started) {
      events.remoteMicMonitoringStarted.dispatch();
      return;
    }
    if (attempt >= MIC_START_ATTEMPTS) return;
    setTimeout(() => {
      if (request === this.monitorRequest) void this.startMicFor(request, attempt + 1);
    }, attempt * 1_500);
  };

  private sendRegister = (silent: boolean, resumed: boolean) => {
    this.transport?.sendEvent({
      t: 'register',
      name: this.currentName,
      id: this.clientId!,
      silent,
      resumed,
      lag: RemoteMicrophoneLagSetting.get(),
    });
  };

  // Whatever was sent while the game was away was dropped, a `register` included. Saying it again
  // is harmless when it did arrive, and puts the phone back on the game's list when it did not.
  private handleRoomBack = () => {
    if (this.connected) this.sendRegister(true, true);
  };

  private joinAttempt = 0;
  private retryAttempt = 0;
  private retryTimeout: ReturnType<typeof setTimeout> | null = null;

  private clearRetry = () => {
    if (this.retryTimeout !== null) {
      clearTimeout(this.retryTimeout);
      this.retryTimeout = null;
    }
  };

  /**
   * One attempt in flight at a time, backing off from a fifth of a second to three - and every
   * phone draws its own jitter, so a room full of them coming back from the same dropped socket
   * doesn't arrive on the relay in lockstep.
   */
  private scheduleRetry = (attempt: () => void) => {
    if (this.retryTimeout !== null) return;

    const delay = Math.min(200 * 2 ** this.retryAttempt, 3_000) + Math.random() * 400;
    this.retryAttempt++;

    this.retryTimeout = setTimeout(() => {
      this.retryTimeout = null;
      attempt();
    }, delay);
  };

  /**
   * The phone is back - screen on, or the network returned. Waiting out the remaining backoff
   * would leave it sitting on a "reconnecting" screen with a perfectly good connection available.
   */
  private retryNow = () => {
    if (!this.reconnecting || this.roomId === null) return;

    this.clearRetry();
    this.retryAttempt = 0;
    events.karaokeConnectionStatusChange.dispatch('reconnecting');
    this.attemptConnect(this.roomId, this.currentName, false);
  };

  public disconnect = () => {
    this.clearRetry();
    this.transport?.close();
  };
}
